import axios, { type AxiosError, type AxiosResponse } from 'axios'
import Cookies from 'js-cookie'
axios.defaults.xsrfCookieName = 'csrftoken'
axios.defaults.xsrfHeaderName = 'X-CSRFToken'

declare global {
  interface Window {
    env: any
  }
}

const http = axios.create({
  baseURL: window.env.REACT_APP_API_URL,
  withCredentials: true,
  headers: {
    'X-CSRFToken': Cookies.get('csrftoken'),
    'Content-Type': 'application/json',
    Accept: 'application/json'
  }
})
http.interceptors.response.use(
  (response) => response
  , async (error) => {
    if (window.location.pathname !== '/login' && (error.response.status === 401 || error.response.data.errors?.detail === 'Authentication credentials were not provided.')) {
      Cookies.remove('sessionid')
      window.location.href = '/login'
    }
    return await Promise.reject(error)
  }
)

interface HttpResponse<T> {
  result: T | null
  status: number
  succeeded: boolean
  message: string | null
  errors: HttpAPIError | null
}

type HttpAPIError = Record<string, string>

function formatErrorMessages (errors: HttpAPIError | null): string {
  if ((errors != null) && typeof errors === 'object') {
    const errorMessages = Object.values(errors)
    return errorMessages.join('\n')
  }
  return errors ?? ''
}

function formatErrorFieldMessages (errors: HttpAPIError | null, prettierKeyNames: Record<string, string> = {}): string {
  const turnSnakeCaseIntoCapitalization = (key: string): string => {
    return key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
  }

  if (errors != null && typeof errors === 'object') {
    const errorMessages = Object.entries(errors).map(([key, value]) => {
      if (key.includes('.') && key.split('.').length > 2) {
        return value
      }
      const formattedKey = prettierKeyNames[key] !== undefined ? prettierKeyNames[key] : turnSnakeCaseIntoCapitalization(key)
      return `${formattedKey} → ${value}`
    })
    return errorMessages.join('\n')
  }
  return errors ?? 'Something went wrong. Please try again.'
}

async function handleHttpRequest<T> (
  request: Promise<AxiosResponse<HttpResponse<T> | undefined>>
): Promise<HttpResponse<T>> {
  try {
    const response = await request

    if (response.status === 204) {
      return { result: null, status: 204, succeeded: true, message: null, errors: null }
    }

    const responseData: HttpResponse<T> | undefined = response.data

    if (responseData != null && typeof responseData === 'object') {
      return responseData
    }
  } catch (error: any) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError
      if (axiosError.response !== null) {
        const response = axiosError.response
        if (response?.data != null && typeof response.data === 'object') {
          return response.data as HttpResponse<T>
        }
      }
    }

    return {
      result: null,
      status: error.status,
      succeeded: false,
      message: error.message,
      errors: {
        error: error.message
      }
    }
  }

  // Default response if no data is available
  return {
    result: null,
    status: 500,
    succeeded: false,
    message: 'No response data',
    errors: null
  }
}

export { http, handleHttpRequest, formatErrorMessages, formatErrorFieldMessages }; export type { HttpResponse, HttpAPIError }
